import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Share2, Twitter, Instagram, Facebook, ArrowRight, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../components/css/fixed.css";

const WhatsAppIcon = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export default function FeedbackSection() {
  const navigate = useNavigate();
  const [, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const card1Ref = useRef(null);
  const card2Ref = useRef(null);
  const trustpilotRef = useRef(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === sectionRef.current) {
              setIsVisible(true);
            }
            entry.target.classList.add("animate-visible");
          }
        });
      },
      { threshold: 0.1, triggerOnce: true }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    if (card1Ref.current) observer.observe(card1Ref.current);
    if (card2Ref.current) observer.observe(card2Ref.current);

    return () => observer.disconnect();
  }, []);

  // Share on X (Twitter)
  const shareOnTwitter = () => {
    const text = encodeURIComponent("Check out Rhythm Growth Lab! https://rhythmgrowthlab.com/");
    const url = encodeURIComponent("https://rhythmgrowthlab.com/");
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      "_blank"
    );
  };

  // Share on Instagram via DM
  const shareOnInstagram = () => {
    const message = "Check out Rhythm Growth Lab! https://rhythmgrowthlab.com/";

    navigator.clipboard.writeText(message).then(() => {
      window.open("https://www.instagram.com/direct/new/", "_blank");
    }).catch(() => {
      window.open("https://www.instagram.com/direct/new/", "_blank");
    });
  };

  // Share on Facebook via Messenger
  const shareOnFacebook = () => {
    const url = encodeURIComponent("https://rhythmgrowthlab.com/");
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      "_blank"
    );
  };

  // Share on WhatsApp
  const shareOnWhatsApp = () => {
    const message = encodeURIComponent(
      "Check out Rhythm Growth Lab! https://rhythmgrowthlab.com/"
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const handleShareReviews = () => {
    navigate("/please-leave-your-reviews");
  };

  return (
    <div className="feedback-container" ref={sectionRef}>
      <div className="bg-glow glow-1"></div>
      <div className="bg-glow glow-2"></div>
      <div className="feedback-content">
        <div className="header-wrapper scroll-animate" ref={headerRef}>
          <h1 className="feedback-title">
            We'd Love Your <span className="feedback-gradient-text">Feedback</span>
          </h1>
          <p className="feedback-subtitle">
            Your opinion helps us grow and serve you better. 
            Share your experience with us!
          </p>
        </div>

        <div className="feedback-grid">
          <div className="feedback-card card-hover scroll-animate" ref={card1Ref}>
            <div className="card-glow"></div>
            <div className="icon-circle blue">
              <MessageCircle size={28} strokeWidth={1.5} />
            </div>
            <h2>Share Reviews</h2>
            <p>Share your thoughts and help us improve</p>
            <div className="rating-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill="#fbbf24" stroke="#fbbf24" />
              ))}
            </div>

            {/* Trustpilot Widget */}
            <div className="trustpilot-wrapper" ref={trustpilotRef}>
              <div 
                className="trustpilot-widget" 
                data-locale="en-US" 
                data-template-id="56278e9abfbbba0bdcd568bc" 
                data-businessunit-id="6a5f9039b201f8ae436cc3e7" 
                data-style-height="52px" 
                data-style-width="100%" 
                data-token="0ff4aa9b-09de-40ec-b766-c998ee8ea958"
              >
                <a 
                  href="https://www.trustpilot.com/review/rhythmgrowthlab.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Trustpilot
                </a>
              </div>
            </div>

            <button className="action-btn blue-btn" onClick={handleShareReviews}>
              Share Your Thoughts <ArrowRight size={18} />
            </button>
          </div>

          {/* Card 2: Share on Social */}
          <div className="feedback-card card-hover scroll-animate" ref={card2Ref}>
            <div className="card-glow"></div>
            <div className="icon-circle purple">
              <Share2 size={28} strokeWidth={1.5} />
            </div>
            <h2>Share on Social</h2>
            <p>Tell your network about us</p>
            <div className="social-icons">
              <button 
                className="social-icon whatsapp" 
                aria-label="Share on WhatsApp"
                onClick={shareOnWhatsApp}
              >
                <WhatsAppIcon size={18} />
              </button>
              <button 
                className="social-icon instagram" 
                aria-label="Share via Instagram DM"
                onClick={shareOnInstagram}
              >
                <Instagram size={18} />
              </button>
              <button 
                className="social-icon facebook" 
                aria-label="Share on Facebook"
                onClick={shareOnFacebook}
              >
                <Facebook size={18} />
              </button>
              <button 
                className="social-icon twitter" 
                aria-label="Share on X"
                onClick={shareOnTwitter}
              >
                <Twitter size={18} />
              </button>
            </div>
            <button className="action-btn outline-btn" onClick={shareOnFacebook}>
              Spread the Word
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}